import { useEffect } from 'react';
import {
  AlertCircleIcon,
  CheckCircle2Icon,
  LoaderCircleIcon,
} from 'lucide-react';
import {Button, Progress, DialogTitle} from '@tinct/web-components';
import { useGetJobById } from '@tinct/core-api';

export type ImportingStepProps = {
  jobId: string | null;
  onJobCompleted?: () => void;
  onRunInBackground?: () => void;
};

const POLL_INTERVAL_MS = 2000;
const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled']);

export const ImportingStep = ({
  jobId,
  onJobCompleted,
  onRunInBackground,
}: ImportingStepProps) => {
  const { data: job } = useGetJobById(
    {
      id: jobId ?? '',
      fields: {
        id: null,
        name: null,
        status: null,
        progress: null,
        errorMessage: null,
      },
    },
    {
      enabled: !!jobId,
      refetchInterval: (query) => {
        const j = query.state.data;
        if (!j?.status) return POLL_INTERVAL_MS;
        return TERMINAL_STATUSES.has(j.status) ? false : POLL_INTERVAL_MS;
      },
    },
  );

  const jobStatus = job?.status ?? null;
  const progress = job?.progress ?? null;
  const errorMessage = job?.errorMessage ?? null;

  const isFailed = jobStatus === 'failed';
  const isCompleted = jobStatus === 'completed';

  useEffect(() => {
    if (isCompleted) onJobCompleted?.();
  }, [isCompleted, onJobCompleted]);

  return (
    <div className="relative overflow-clip rounded-3xl border bg-background shadow-lg">
      {/* Header */}
      <div className="p-8">
        <DialogTitle className="text-2xl font-semibold tracking-[-0.4px] text-center">
          Importing customers
        </DialogTitle>
      </div>

      {/* Content */}
      <div className="px-8 pb-8">
        <div className="flex flex-col items-center justify-center gap-6 rounded-lg p-6">
          <div
            className={
              'flex items-center justify-center size-10 rounded-lg ' +
              (isFailed
                ? 'bg-destructive/10 text-destructive'
                : isCompleted
                  ? 'bg-emerald-100 text-emerald-600'
                  : 'bg-muted')
            }
          >
            {isFailed ? (
              <AlertCircleIcon className="size-6" />
            ) : isCompleted ? (
              <CheckCircle2Icon className="size-6" />
            ) : (
              <LoaderCircleIcon className="size-6 animate-spin" />
            )}
          </div>
          <div className="flex flex-col items-center gap-2 text-center">
            <p className="text-lg font-medium">
              {isFailed
                ? 'Import failed'
                : isCompleted
                  ? 'Finalizing…'
                  : 'Importing customers…'}
            </p>
            <p className="text-sm text-muted-foreground">
              {isFailed
                ? errorMessage ??
                  'Something went wrong while importing. Please try again.'
                : 'Please wait — this may take a few moments.'}
            </p>
          </div>

          {!isFailed && progress !== null && progress >= 0 && (
            <div className="w-full max-w-sm">
              <Progress value={Math.min(100, Math.max(0, progress))} />
            </div>
          )}

          {!isFailed && !isCompleted && onRunInBackground && (
            <Button type="button" onClick={onRunInBackground}>
              Run in background
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
